import React, { useState } from 'react';
import {
  CheckCircle2, BadgeCheck, ChevronRight,
  PawPrint, Shield, Heart, Trophy,
  Stethoscope, CalendarClock, Pill, BarChart2,
  Home as HomeIcon, Bell,
  Award, Sparkles,
  MapPin, Navigation, Map as MapIcon,
  Users, Globe, Scissors,
  QrCode, Activity, Zap,
} from 'lucide-react';

/* ─── Stars helper ─────────────────────────────────────────────── */
const Stars: React.FC<{ value: number; size?: number }> = ({ value, size = 10 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <svg key={i} width={size} height={size} viewBox="0 0 20 20"
        fill={i <= Math.round(value) ? '#F59E0B' : '#E2E8F0'}>
        <path d="M10 1l2.7 5.5 6.1.9-4.4 4.3 1 6-5.4-2.8L4.6 17.7l1-6L1.2 7.4l6.1-.9z" />
      </svg>
    ))}
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   1. DUEÑOS — /features  (emerald)
   ───────────────────────────────────────────────────────────────── */
export const DuenosContent: React.FC = () => {
  const [hovered, setHovered] = useState<string | null>(null);

  const features = [
    { id: 'f1', icon: Heart,  grad: 'from-emerald-700 to-teal-500', title: 'Carnet Digital',   sub: 'Historial siempre en tu bolsillo', tags: ['Vacunas', 'Consultas', 'Historial'] },
    { id: 'f2', icon: Shield, grad: 'from-red-600 to-orange-500',   title: 'GPS + Modo SOS',  sub: 'Rastreo en tiempo real',           tags: ['GPS Live', 'Geovallas', 'SOS']     },
    { id: 'f3', icon: Users,  grad: 'from-sky-700 to-blue-500',     title: 'Comunidad',       sub: 'Red social para mascotas',         tags: ['Feed', 'Grupos', 'Playdates']      },
    { id: 'f4', icon: Trophy, grad: 'from-amber-600 to-yellow-400', title: 'Academia AP',     sub: 'Entrenamiento gamificado',         tags: ['Retos', 'AmigoPoints', 'Niveles']  },
  ];

  return (
    <>
      {/* ── FEATURE CARDS ── */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <p className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.3em] mb-3">Funcionalidades principales</p>
          <h2 className="text-2xl lg:text-3xl font-black text-slate-900 mb-10">Todo lo que incluye tu cuenta gratuita</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map(f => (
              <div
                key={f.id}
                onMouseEnter={() => setHovered(f.id)}
                onMouseLeave={() => setHovered(null)}
                className={`bg-white border rounded-[2rem] overflow-hidden cursor-pointer transition-all duration-300 ${hovered === f.id ? 'border-emerald-200 shadow-2xl shadow-emerald-500/10 -translate-y-1' : 'border-slate-100 hover:shadow-lg'}`}
              >
                <div className={`h-28 bg-gradient-to-br ${f.grad} relative overflow-hidden flex items-end p-5`}>
                  <div className="absolute right-4 top-4 w-20 h-20 rounded-full border-4 border-white/10 animate-[spin_20s_linear_infinite]" />
                  <div className="absolute right-9 top-7 w-10 h-10 rounded-full border-4 border-white/10 animate-[spin_15s_linear_infinite_reverse]" />
                  <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center shadow-lg z-10 shrink-0">
                    <f.icon size={22} className="text-white" />
                  </div>
                  <div className="ml-3 z-10 flex gap-1 flex-wrap">
                    {f.tags.map(t => (
                      <span key={t} className="text-[7px] font-black uppercase tracking-widest bg-white/20 text-white px-2 py-0.5 rounded-full">{t}</span>
                    ))}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-sm font-black text-slate-800 leading-tight mb-0.5">{f.title}</h3>
                  <p className="text-[10px] font-bold text-slate-400 mb-3">{f.sub}</p>
                  <button className="w-full py-2.5 bg-slate-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5">
                    Descubrir <ChevronRight size={10} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS BANNER (foto de fondo) ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1450778869180-41d0601e046e?auto=format&fit=crop&q=80&w=1600" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0 bg-slate-900/55" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-8 py-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { icon: PawPrint, value: '+50k',  label: 'Mascotas registradas', bg: 'bg-emerald-100' },
              { icon: Shield,   value: '98%',   label: 'Recuperación SOS',     bg: 'bg-red-100'     },
              { icon: Heart,    value: '24/7',  label: 'Historial disponible', bg: 'bg-sky-100'     },
              { icon: Trophy,   value: '1.2k',  label: 'AmigoPoints máximos',  bg: 'bg-amber-100'   },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-[2.5rem] p-5 lg:p-7 flex flex-col items-center text-center`}>
                <div className="w-11 h-11 bg-emerald-500 rounded-full flex items-center justify-center mb-3 shadow-md">
                  <s.icon size={18} className="text-white" />
                </div>
                <p className="text-3xl lg:text-4xl font-black text-slate-900">{s.value}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1 leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INFO PANELS ── */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-8">
          <p className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.3em] mb-10">Lo que más preguntan los dueños</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Shield,   topic: 'GPS y seguridad',           text: 'El rastreo GPS muestra la ubicación de tu mascota en tiempo real. Configura geovallas — si tu mascota sale de la zona segura, recibes una alerta inmediata en tu celular.' },
              { icon: Heart,    topic: 'Historial médico digital',  text: 'El carnet digital reúne todas las vacunas, desparasitaciones y consultas en un solo lugar. Tu veterinaria actualiza el historial automáticamente después de cada visita.' },
              { icon: Bell,     topic: 'Red SOS comunitaria',       text: 'Al activar el modo SOS, todos los usuarios de Amigo Peludo en el área cercana reciben una alerta con foto y descripción de tu mascota — como un Amber Alert para mascotas.' },
              { icon: Users,    topic: 'Comunidad activa',          text: 'La red social de Amigo Peludo conecta a dueños por ciudad, raza y estilo de vida. Organiza playdates, únete a grupos y comparte los mejores momentos con tu mascota.' },
              { icon: Trophy,   topic: 'Entrenamiento gamificado',  text: 'La Academia AP convierte el adiestramiento en un juego con retos, niveles y AmigoPoints que canjeas por beneficios reales dentro de la plataforma.' },
              { icon: QrCode,   topic: 'QR de emergencia',          text: 'Cada mascota tiene un código QR único. Cualquier persona que encuentre a tu mascota puede escanearlo para ver sus datos de contacto y activar el modo SOS desde su teléfono.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3">
                  <item.icon size={18} className="text-emerald-600" />
                </div>
                <p className="text-[9px] font-black uppercase tracking-widest text-emerald-600 mb-1.5">{item.topic}</p>
                <p className="text-[12px] font-medium text-slate-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

/* ─────────────────────────────────────────────────────────────────
   2. VETERINARIAS — /veterinarias  (blue)
   ───────────────────────────────────────────────────────────────── */
const DEMO_VETS = [
  { id: 'v1', name: 'Clínica San Bernardo',      city: 'Santiago Centro', specialty: 'Medicina General · Urgencias 24h', stars: 4.9, reviews: 124, grad: 'from-blue-900 to-blue-700',   init: 'SB', services: ['Consultas', 'Cirugía', 'Urgencias'],       verified: true, open: true  },
  { id: 'v2', name: 'Vet Express Las Condes',    city: 'Las Condes',      specialty: 'Urgencias · Radiología',           stars: 4.8, reviews:  98, grad: 'from-indigo-800 to-blue-600', init: 'VE', services: ['Urgencias', 'Rayos X', 'Laboratorio'],    verified: true, open: true  },
  { id: 'v3', name: 'Centro Veterinario Ñuñoa', city: 'Ñuñoa',           specialty: 'Cardiología · Oftalmología',       stars: 4.7, reviews:  77, grad: 'from-sky-800 to-sky-600',     init: 'CV', services: ['Cardiología', 'Oftalmología'],              verified: true, open: false },
];

const DemoVetCard: React.FC<{ vet: typeof DEMO_VETS[0]; hovered: boolean; onHover: (id: string | null) => void }> = ({ vet, hovered, onHover }) => (
  <div
    onMouseEnter={() => onHover(vet.id)}
    onMouseLeave={() => onHover(null)}
    className={`bg-white border rounded-[2rem] overflow-hidden group cursor-pointer transition-all duration-300 ${hovered ? 'border-blue-200 shadow-2xl shadow-blue-500/10 -translate-y-1' : 'border-slate-100 hover:shadow-lg'}`}
  >
    <div className={`h-28 bg-gradient-to-br ${vet.grad} flex items-end p-5 relative overflow-hidden`}>
      <div className="absolute right-4 top-4 w-20 h-20 rounded-full border-4 border-white/10 animate-[spin_20s_linear_infinite]" />
      <div className="absolute right-9 top-7 w-10 h-10 rounded-full border-4 border-white/10 animate-[spin_15s_linear_infinite_reverse]" />
      <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center text-2xl font-black text-white shadow-lg shrink-0 z-10">
        {vet.init}
      </div>
      <div className="ml-3 z-10">
        <div className="flex items-center gap-2 mb-1">
          {vet.open && <span className="text-[7px] font-black uppercase tracking-widest bg-emerald-400 text-white px-2 py-0.5 rounded-full">Abierto</span>}
          {vet.verified && <BadgeCheck size={13} className="text-white/80" />}
        </div>
        <div className="flex items-center gap-1">
          <Stars value={vet.stars} size={10} />
          <span className="text-white/80 text-[9px] font-black">{vet.stars} ({vet.reviews})</span>
        </div>
      </div>
    </div>
    <div className="p-5">
      <h3 className="text-sm font-black text-slate-800 leading-tight mb-1">{vet.name}</h3>
      <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-bold mb-2">
        <MapPin size={10} /> {vet.city}
      </div>
      <p className="text-[10px] font-bold text-slate-500 mb-3">{vet.specialty}</p>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {vet.services.map(s => (
          <span key={s} className="text-[8px] font-black uppercase tracking-wide bg-blue-50 text-blue-800 px-2.5 py-1 rounded-full">{s}</span>
        ))}
      </div>
      <button className="w-full py-2.5 bg-slate-900 group-hover:bg-[#1E3A8A] text-white rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5">
        Ver perfil <ChevronRight size={11} />
      </button>
    </div>
  </div>
);

export const VeterinariasContent: React.FC = () => {
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <>
      {/* ── DIRECTORIO DEMO ── */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <p className="text-[10px] font-black text-[#1E3A8A] uppercase tracking-[0.3em] mb-3">Directorio de veterinarias</p>
          <h2 className="text-2xl lg:text-3xl font-black text-slate-900 mb-8">Clínicas verificadas en Amigo Peludo</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {DEMO_VETS.map(v => (
              <DemoVetCard key={v.id} vet={v} hovered={hovered === v.id} onHover={setHovered} />
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS FOTO FONDO ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1629015986-7d3b71b8b44c?auto=format&fit=crop&q=80&w=1600" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0 bg-blue-950/60" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-8 py-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { icon: Award,         value: '+1.200', label: 'Clínicas activas',          bg: 'bg-sky-100'    },
              { icon: CalendarClock, value: '3×',     label: 'Más retorno clientes',      bg: 'bg-blue-100'   },
              { icon: Pill,          value: '24/7',   label: 'Historial disponible',      bg: 'bg-indigo-100' },
              { icon: BarChart2,     value: '60%',    label: 'Más citas con recordatorio', bg: 'bg-sky-100'   },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-[2.5rem] p-5 lg:p-7 flex flex-col items-center text-center`}>
                <div className="w-11 h-11 bg-[#1E3A8A] rounded-full flex items-center justify-center mb-3 shadow-md">
                  <s.icon size={18} className="text-white" />
                </div>
                <p className="text-3xl lg:text-4xl font-black text-slate-900">{s.value}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1 leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MÓDULOS DEL SISTEMA ── */}
      <section className="py-16 bg-[#EFF6FF]">
        <div className="max-w-6xl mx-auto px-8">
          <p className="text-[10px] font-black text-[#1E3A8A] uppercase tracking-[0.3em] mb-3">Módulos del sistema</p>
          <h2 className="text-2xl lg:text-3xl font-black text-slate-900 mb-10">Todo lo que necesita tu clínica, en una sola pantalla</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Stethoscope,   color: 'text-[#1E3A8A]', bg: 'bg-blue-50',    title: 'Historia Clínica Digital',     desc: 'Consultas, diagnósticos, tratamientos y recetas en una ficha unificada por paciente. Accesible desde celular o PC.' },
              { icon: CalendarClock, color: 'text-sky-600',   bg: 'bg-sky-50',     title: 'Agenda Online',                desc: 'Citas con confirmación automática, recordatorios y bloqueo de horarios. Los dueños agendan sin llamar.' },
              { icon: Activity,      color: 'text-indigo-600', bg: 'bg-indigo-50',  title: 'Hospitalización EN VIVO',      desc: 'Panel de pacientes internados con signos vitales, tratamientos y alertas de parámetros fuera de rango.' },
              { icon: Pill,          color: 'text-blue-600',  bg: 'bg-blue-50',    title: 'Farmacia e Inventario',        desc: 'Stock en tiempo real, alertas de mínimo, POS integrado con Transbank y generación de recetas en PDF.' },
              { icon: MapPin,        color: 'text-sky-700',   bg: 'bg-sky-50',     title: 'Visibilidad en el Mapa',       desc: 'Tu clínica aparece en el directorio de Amigo Peludo y en el mapa para miles de dueños de mascotas.' },
              { icon: Bell,          color: 'text-[#1E3A8A]', bg: 'bg-blue-50',    title: 'Recordatorios Automáticos',   desc: 'El sistema notifica a los dueños cuando se acercan las fechas de vacuna, desparasitación o control anual.' },
            ].map((m, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-lg transition-all duration-200">
                <div className={`w-10 h-10 rounded-xl ${m.bg} flex items-center justify-center mb-3`}>
                  <m.icon size={18} className={m.color} />
                </div>
                <h3 className="text-[13px] font-black text-slate-800 mb-2">{m.title}</h3>
                <p className="text-[11px] font-medium text-slate-500 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

/* ─────────────────────────────────────────────────────────────────
   3. ALBERGUES — /albergues  (rose/pink)
   ───────────────────────────────────────────────────────────────── */
const DEMO_SHELTERS = [
  { id: 's1', name: 'Refugio Amigos Peludos',   city: 'Pudahuel',   residents: 48, tagline: 'Adopción responsable y amor incondicional', grad: 'from-pink-700 to-rose-600',    init: 'AP', verified: true, species: ['Perros', 'Gatos'] },
  { id: 's2', name: 'Huellas Felices Santiago', city: 'Maipú',      residents: 31, tagline: 'Cada animal merece una segunda oportunidad', grad: 'from-rose-700 to-pink-500',   init: 'HF', verified: true, species: ['Perros'] },
  { id: 's3', name: 'Refugio Patitas',          city: 'La Florida', residents: 24, tagline: 'Rescate, rehabilitación y adopción',          grad: 'from-fuchsia-700 to-rose-600', init: 'RP', verified: true, species: ['Perros', 'Gatos', 'Conejos'] },
];

const DemoShelterCard: React.FC<{ shelter: typeof DEMO_SHELTERS[0]; hovered: boolean; onHover: (id: string | null) => void }> = ({ shelter, hovered, onHover }) => (
  <div
    onMouseEnter={() => onHover(shelter.id)}
    onMouseLeave={() => onHover(null)}
    className={`bg-white border rounded-[2rem] overflow-hidden group cursor-pointer transition-all duration-300 ${hovered ? 'border-rose-200 shadow-2xl shadow-rose-500/10 -translate-y-1' : 'border-slate-100 hover:shadow-lg'}`}
  >
    <div className={`h-28 bg-gradient-to-br ${shelter.grad} flex items-end p-5 relative overflow-hidden`}>
      <div className="absolute inset-0 flex items-center justify-end pr-4 pt-4 opacity-20">
        {[0, 1, 2, 3, 4].map(i => <Heart key={i} size={16 + i * 5} className="absolute text-white" style={{ right: `${10 + i * 15}%`, top: `${10 + i * 12}%`, transform: `rotate(${i * 15 - 20}deg)` }} />)}
      </div>
      <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center text-2xl font-black text-white shadow-lg shrink-0 z-10">
        {shelter.init}
      </div>
      <div className="ml-3 z-10">
        <div className="flex items-center gap-2 mb-1">
          {shelter.verified && <BadgeCheck size={13} className="text-white/80" />}
          <span className="text-[7px] font-black uppercase tracking-widest bg-white/20 text-white px-2 py-0.5 rounded-full">{shelter.residents} residentes</span>
        </div>
        <div className="flex gap-1 mt-1">
          {shelter.species.slice(0, 3).map(sp => (
            <span key={sp} className="text-[7px] font-black text-white/70 bg-white/10 px-1.5 py-0.5 rounded-full">{sp}</span>
          ))}
        </div>
      </div>
    </div>
    <div className="p-5">
      <h3 className="text-sm font-black text-slate-800 leading-tight mb-1">{shelter.name}</h3>
      <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-bold mb-2">
        <MapPin size={10} /> {shelter.city}
      </div>
      <p className="text-[10px] font-bold text-slate-500 italic mb-3">"{shelter.tagline}"</p>
      <div className="bg-rose-50 rounded-2xl p-3 flex items-center gap-3 mb-4">
        <Heart size={15} className="text-rose-400 shrink-0" />
        <div>
          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">En busca de hogar</p>
          <p className="text-sm font-black text-rose-600">{shelter.residents} animales</p>
        </div>
      </div>
      <button className="w-full py-2.5 bg-slate-900 group-hover:bg-rose-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5">
        Ver animales <ChevronRight size={11} />
      </button>
    </div>
  </div>
);

export const AlberguesContent: React.FC = () => {
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <>
      {/* ── DIRECTORIO DEMO ── */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <p className="text-[10px] font-black text-rose-600 uppercase tracking-[0.3em] mb-3">Refugios verificados</p>
          <h2 className="text-2xl lg:text-3xl font-black text-slate-900 mb-8">Adopta una mascota hoy</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {DEMO_SHELTERS.map(s => (
              <DemoShelterCard key={s.id} shelter={s} hovered={hovered === s.id} onHover={setHovered} />
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS FOTO FONDO ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&q=80&w=1600" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0 bg-rose-950/55" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-8 py-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { icon: Heart,    value: '+12k', label: 'Adopciones logradas',  bg: 'bg-rose-100'    },
              { icon: HomeIcon, value: '320+', label: 'Refugios activos',     bg: 'bg-pink-100'    },
              { icon: Users,    value: '4.8k', label: 'Voluntarios activos',  bg: 'bg-fuchsia-100' },
              { icon: Award,    value: '98%',  label: 'Seguimiento adopción', bg: 'bg-rose-100'    },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-[2.5rem] p-5 lg:p-7 flex flex-col items-center text-center`}>
                <div className="w-11 h-11 bg-rose-500 rounded-full flex items-center justify-center mb-3 shadow-md">
                  <s.icon size={18} className="text-white" />
                </div>
                <p className="text-3xl lg:text-4xl font-black text-slate-900">{s.value}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1 leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INFO PANELS ── */}
      <section className="py-16 bg-[#FFF1F2]">
        <div className="max-w-6xl mx-auto px-8">
          <p className="text-[10px] font-black text-rose-600 uppercase tracking-[0.3em] mb-10">Herramientas para tu refugio</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Heart,    topic: 'Gestión de animales',        text: 'Ficha individual por animal: especie, raza, edad, historial médico, estado de vacunación, microchip y protocolo sanitario de cuarentena. Todo en un solo sistema.' },
              { icon: Users,    topic: 'Adopciones digitales',       text: 'Publica animales en adopción con ficha completa. Los formularios de solicitud, entrevista y seguimiento post-adopción a los 15 días, 3 meses y 1 año son automáticos.' },
              { icon: Globe,    topic: 'Publicación en redes',       text: 'Genera automáticamente cards de adopción optimizadas para Instagram, Facebook y WhatsApp, con foto, descripción y personalidad de cada animal.' },
              { icon: Award,    topic: 'Voluntariado organizado',    text: 'Registra voluntarios, asigna tareas (paseos, fotografía, socialización), lleva el registro de horas y comunícate con el equipo desde la app.' },
              { icon: Shield,   topic: 'Donaciones online',          text: 'Recibe donaciones únicas o recurrentes, gestiona donantes con historial, emite certificados y crea campañas de crowdfunding para emergencias veterinarias.' },
              { icon: Bell,     topic: 'Ferias de adopción',         text: 'Publica eventos de adopción en el calendario de la comunidad, envía alertas a usuarios interesados y gestiona inscripciones desde la plataforma.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200">
                <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center mb-3">
                  <item.icon size={18} className="text-rose-600" />
                </div>
                <p className="text-[9px] font-black uppercase tracking-widest text-rose-600 mb-1.5">{item.topic}</p>
                <p className="text-[12px] font-medium text-slate-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

/* ─────────────────────────────────────────────────────────────────
   4. CRIADEROS — /criaderos  (amber)
   ───────────────────────────────────────────────────────────────── */
const DEMO_BREEDERS = [
  { id: 'b1', name: 'Kennel Los Alpes',      city: 'Las Condes',  breed: 'Golden Retriever', litters: 14, grad: 'from-amber-800 to-amber-600',  init: 'LA', verified: true, certified: 'FCI · KCC'   },
  { id: 'b2', name: 'Criadero Pampas Sur',   city: 'Maipú',       breed: 'Labrador',         litters:  9, grad: 'from-yellow-700 to-amber-500', init: 'PS', verified: true, certified: 'KCC Chile'   },
  { id: 'b3', name: 'Cattery Azul Profundo', city: 'Providencia', breed: 'Maine Coon',       litters:  6, grad: 'from-orange-700 to-amber-600', init: 'CA', verified: true, certified: 'TICA · FIFe' },
];

const DemoBreederCard: React.FC<{ breeder: typeof DEMO_BREEDERS[0]; hovered: boolean; onHover: (id: string | null) => void }> = ({ breeder, hovered, onHover }) => (
  <div
    onMouseEnter={() => onHover(breeder.id)}
    onMouseLeave={() => onHover(null)}
    className={`bg-white border rounded-[2rem] overflow-hidden group cursor-pointer transition-all duration-300 ${hovered ? 'border-amber-200 shadow-2xl shadow-amber-500/10 -translate-y-1' : 'border-slate-100 hover:shadow-lg'}`}
  >
    <div className={`h-28 bg-gradient-to-br ${breeder.grad} flex items-end p-5 relative overflow-hidden`}>
      {[...Array(6)].map((_, i) => (
        <div key={i} className="absolute w-1.5 h-1.5 bg-white/30 rounded-full" style={{ left: `${10 + i * 15}%`, top: `${15 + (i % 3) * 20}%` }} />
      ))}
      <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center text-2xl font-black text-white shadow-lg shrink-0 z-10">
        {breeder.init}
      </div>
      <div className="ml-3 z-10">
        <div className="flex items-center gap-2 mb-1">
          {breeder.verified && <BadgeCheck size={13} className="text-white/80" />}
          <span className="text-[7px] font-black uppercase tracking-widest bg-amber-400/80 text-white px-2 py-0.5 rounded-full">Pedigree ✓</span>
        </div>
        {breeder.certified && <p className="text-[8px] text-white/70 font-bold mt-1">{breeder.certified}</p>}
      </div>
    </div>
    <div className="p-5">
      <h3 className="text-sm font-black text-slate-800 leading-tight mb-1">{breeder.name}</h3>
      <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-bold mb-2">
        <MapPin size={10} /> {breeder.city}
      </div>
      <p className="text-[10px] font-bold text-slate-500 mb-3">Especialista en {breeder.breed}</p>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="bg-amber-50 rounded-xl p-2.5 text-center">
          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Camadas</p>
          <p className="text-base font-black text-amber-700">{breeder.litters}</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-2.5 text-center">
          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Raza</p>
          <p className="text-[9px] font-black text-slate-700 leading-tight">{breeder.breed.split(' ')[0]}</p>
        </div>
      </div>
      <button className="w-full py-2.5 bg-slate-900 group-hover:bg-amber-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5">
        Ver criadero <ChevronRight size={11} />
      </button>
    </div>
  </div>
);

export const CriaderoContent: React.FC = () => {
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <>
      {/* ── DIRECTORIO DEMO ── */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <p className="text-[10px] font-black text-amber-600 uppercase tracking-[0.3em] mb-3">Directorio de criaderos</p>
          <h2 className="text-2xl lg:text-3xl font-black text-slate-900 mb-8">Criaderos responsables verificados en Chile</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {DEMO_BREEDERS.map(b => (
              <DemoBreederCard key={b.id} breeder={b} hovered={hovered === b.id} onHover={setHovered} />
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS FOTO FONDO ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1425082661705-1834bfd09dca?auto=format&fit=crop&q=80&w=1600" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0 bg-amber-950/60" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-8 py-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { icon: Award,    value: '480+', label: 'Criaderos verificados',  bg: 'bg-amber-100'  },
              { icon: Sparkles, value: '2.4k', label: 'Camadas publicadas',     bg: 'bg-yellow-100' },
              { icon: Shield,   value: '100%', label: 'Health tests validados', bg: 'bg-orange-100' },
              { icon: Globe,    value: 'FCI',  label: 'Compatible pedigree',    bg: 'bg-amber-100'  },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-[2.5rem] p-5 lg:p-7 flex flex-col items-center text-center`}>
                <div className="w-11 h-11 bg-amber-500 rounded-full flex items-center justify-center mb-3 shadow-md">
                  <s.icon size={18} className="text-white" />
                </div>
                <p className="text-3xl lg:text-4xl font-black text-slate-900">{s.value}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1 leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INFO PANELS ── */}
      <section className="py-16 bg-[#FFFBEB]">
        <div className="max-w-6xl mx-auto px-8">
          <p className="text-[10px] font-black text-amber-600 uppercase tracking-[0.3em] mb-10">Todo lo que necesita tu criadero</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Award,         topic: 'Pedigree y linaje',         text: 'Registra cada ejemplar con su número de pedigree KCC o FCI. El sistema cruza genealogías para verificar linajes y cumplimiento de estándares de raza antes de autorizar un cruce.' },
              { icon: Scissors,      topic: 'Gestión de camadas',        text: 'Registra gestaciones con fechas estimadas de parto, lleva control individual de cada cachorro desde el nacimiento y publica la camada disponible para compradores verificados.' },
              { icon: Shield,        topic: 'Health tests',              text: 'Registra radiografías de displasia (OFA/PennHIP), test ocular CAER, test de sordera BAER y paneles genéticos. Todo certificado en el perfil del criadero para compradores.' },
              { icon: Globe,         topic: 'Catálogo público',          text: 'Los compradores buscan cachorros en Amigo Peludo por raza, ciudad y disponibilidad. Tu criadero aparece con sello verificado, fotos y documentación completa.' },
              { icon: CalendarClock, topic: 'Agenda reproductiva',       text: 'El sistema registra ciclos estrales, programa alertas de celo, hace seguimiento de gestaciones y notifica al criadero cuando se acerca la fecha estimada de parto.' },
              { icon: QrCode,        topic: 'QR por ejemplar',           text: 'Cada cachorro recibe un QR único que el comprador puede escanear para ver pedigree de padres, vacunas, health tests y datos del criadero sin instalar la app.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-3">
                  <item.icon size={18} className="text-amber-700" />
                </div>
                <p className="text-[9px] font-black uppercase tracking-widest text-amber-700 mb-1.5">{item.topic}</p>
                <p className="text-[12px] font-medium text-slate-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

/* ─────────────────────────────────────────────────────────────────
   5. GPS / MAPA — /gps  (violet)
   ───────────────────────────────────────────────────────────────── */
export const GPSContent: React.FC = () => (
  <>
    {/* ── FEATURE CARDS GPS ── */}
    <section className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-8">
        <p className="text-[10px] font-black text-violet-600 uppercase tracking-[0.3em] mb-3">Qué incluye el sistema GPS</p>
        <h2 className="text-2xl lg:text-3xl font-black text-slate-900 mb-10">Rastreo, alertas y red SOS en una sola plataforma</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Navigation, grad: 'from-violet-700 to-indigo-600', title: 'GPS en Vivo',     sub: 'Ubicación actualizada',     tags: ['Tiempo real', '4G']    },
            { icon: MapIcon,    grad: 'from-indigo-700 to-violet-500', title: 'Geovallas',       sub: 'Zona segura personalizada', tags: ['Alertas', 'Zonas']     },
            { icon: Activity,   grad: 'from-purple-700 to-violet-600', title: 'Historial Rutas', sub: 'Últimos 30 días',           tags: ['Rutas', 'Mapa']        },
            { icon: Zap,        grad: 'from-violet-800 to-indigo-700', title: 'SOS Instantáneo', sub: 'Alerta comunitaria masiva', tags: ['SOS', 'Red']           },
          ].map(f => (
            <div key={f.title} className="bg-white border border-slate-100 rounded-[2rem] overflow-hidden hover:-translate-y-1 hover:shadow-2xl hover:shadow-violet-500/10 transition-all duration-300">
              <div className={`h-28 bg-gradient-to-br ${f.grad} flex items-end p-5 relative overflow-hidden`}>
                <div className="absolute right-4 top-4 w-20 h-20 rounded-full border-4 border-white/10 animate-[spin_20s_linear_infinite]" />
                <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center shadow-lg z-10 shrink-0">
                  <f.icon size={22} className="text-white" />
                </div>
                <div className="ml-3 z-10 flex gap-1 flex-wrap">
                  {f.tags.map(t => <span key={t} className="text-[7px] font-black uppercase tracking-widest bg-white/20 text-white px-2 py-0.5 rounded-full">{t}</span>)}
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-sm font-black text-slate-800 mb-0.5">{f.title}</h3>
                <p className="text-[10px] font-bold text-slate-400 mb-3">{f.sub}</p>
                <button className="w-full py-2.5 bg-slate-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-1.5">
                  Ver más <ChevronRight size={10} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── STATS FOTO ── */}
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src="https://images.unsplash.com/photo-1516734212186-a967f81ad0d7?auto=format&fit=crop&q=80&w=1600" alt="" className="w-full h-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-violet-950/60" />
      </div>
      <div className="relative z-10 max-w-6xl mx-auto px-8 py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {[
            { icon: PawPrint,   value: '8.2k',  label: 'Mascotas rastreadas hoy', bg: 'bg-violet-100' },
            { icon: Shield,     value: '98%',   label: 'Recuperación SOS',        bg: 'bg-indigo-100' },
            { icon: MapPin,     value: '2.5k',  label: 'Zonas pet-friendly',      bg: 'bg-purple-100' },
            { icon: Zap,        value: '<2min', label: 'Respuesta SOS media',     bg: 'bg-violet-100' },
          ].map(s => (
            <div key={s.label} className={`${s.bg} rounded-[2.5rem] p-5 lg:p-7 flex flex-col items-center text-center`}>
              <div className="w-11 h-11 bg-violet-600 rounded-full flex items-center justify-center mb-3 shadow-md">
                <s.icon size={18} className="text-white" />
              </div>
              <p className="text-3xl lg:text-4xl font-black text-slate-900">{s.value}</p>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1 leading-tight">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── INFO PANELS ── */}
    <section className="py-16 bg-[#F5F3FF]">
      <div className="max-w-6xl mx-auto px-8">
        <p className="text-[10px] font-black text-violet-600 uppercase tracking-[0.3em] mb-10">Preguntas frecuentes sobre GPS</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: Navigation, topic: 'GPS vs microchip',           text: 'El microchip identifica a tu mascota si alguien la lleva a una veterinaria. El GPS te dice dónde está en tiempo real. Son complementarios: el microchip es obligatorio por ley, el GPS es tu tranquilidad diaria.' },
            { icon: MapIcon,    topic: 'Zonas pet-friendly',         text: 'El mapa de Amigo Peludo incluye más de 2.500 puntos de interés: playas que permiten mascotas, parques con área sin correa, camping pet-friendly y parques caninos en toda Chile.' },
            { icon: Zap,        topic: 'Cómo funciona el SOS',       text: 'Al activar el SOS, todos los usuarios de Amigo Peludo en el área reciben una alerta con foto, nombre y último punto GPS de tu mascota. La respuesta media de la comunidad es menor a 2 minutos.' },
            { icon: QrCode,     topic: 'Collar QR de emergencia',    text: 'La placa QR de Amigo Peludo se graba con tu perfil. Si alguien encuentra a tu mascota y escanea el código, ve tus datos de contacto inmediatamente, sin necesidad de tener la app instalada.' },
            { icon: Bell,       topic: 'Alertas de temperatura',     text: 'En días de calor extremo (+30°C), el sistema envía alertas automáticas a los dueños en zonas afectadas y recomienda no sacar a la mascota en horarios de máxima radiación.' },
            { icon: Activity,   topic: 'Historial de actividad',     text: 'Consulta las rutas que hizo tu mascota los últimos 30 días, el tiempo que estuvo activa y las zonas que frecuenta. Útil para detectar cambios de comportamiento o actividad inusual.' },
          ].map((item, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center mb-3">
                <item.icon size={18} className="text-violet-600" />
              </div>
              <p className="text-[9px] font-black uppercase tracking-widest text-violet-600 mb-1.5">{item.topic}</p>
              <p className="text-[12px] font-medium text-slate-600 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </>
);

/* ─────────────────────────────────────────────────────────────────
   6. COMUNIDAD — /comunidad  (orange)
   ───────────────────────────────────────────────────────────────── */
export const ComunidadContent: React.FC = () => {
  const [hovered, setHovered] = useState<string | null>(null);

  const profiles = [
    { id: 'p1', name: 'Max — Golden Retriever', owner: 'Familia Rodríguez', city: 'Las Condes',  followers: '2.4k', grad: 'from-orange-500 to-amber-400',  init: 'M', posts: 84,  verified: true  },
    { id: 'p2', name: 'Luna — French Bulldog',  owner: 'Catalina P.',       city: 'Providencia', followers: '1.8k', grad: 'from-rose-500 to-orange-400',   init: 'L', posts: 67,  verified: true  },
    { id: 'p3', name: 'Simba — Maine Coon',     owner: 'Pablo & Sofía',     city: 'Ñuñoa',       followers: '3.1k', grad: 'from-amber-600 to-orange-500',  init: 'S', posts: 112, verified: false },
  ];

  return (
    <>
      {/* ── PERFILES POPULARES ── */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <p className="text-[10px] font-black text-orange-500 uppercase tracking-[0.3em] mb-3">Perfiles populares</p>
          <h2 className="text-2xl lg:text-3xl font-black text-slate-900 mb-8">Las mascotas más seguidas de la comunidad</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {profiles.map(p => (
              <div
                key={p.id}
                onMouseEnter={() => setHovered(p.id)}
                onMouseLeave={() => setHovered(null)}
                className={`bg-white border rounded-[2rem] overflow-hidden cursor-pointer transition-all duration-300 ${hovered === p.id ? 'border-orange-200 shadow-2xl shadow-orange-500/10 -translate-y-1' : 'border-slate-100 hover:shadow-lg'}`}
              >
                <div className={`h-28 bg-gradient-to-br ${p.grad} flex items-end p-5 relative overflow-hidden`}>
                  <PawPrint size={50} className="absolute -top-2 -right-2 text-white/15" />
                  <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center text-2xl font-black text-white shadow-lg shrink-0 z-10">
                    {p.init}
                  </div>
                  <div className="ml-3 z-10">
                    <div className="flex items-center gap-2 mb-1">
                      {p.verified && <BadgeCheck size={13} className="text-white/80" />}
                      <span className="text-[7px] font-black uppercase tracking-widest bg-white/20 text-white px-2 py-0.5 rounded-full">{p.followers} seguidores</span>
                    </div>
                    <p className="text-[8px] text-white/70 font-bold">{p.posts} posts</p>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-sm font-black text-slate-800 leading-tight mb-1">{p.name}</h3>
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-bold mb-2">
                    <MapPin size={10} /> {p.city}
                  </div>
                  <p className="text-[10px] font-bold text-slate-500 mb-4">Familia: {p.owner}</p>
                  <button className="w-full py-2.5 bg-slate-900 hover:bg-orange-500 text-white rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5">
                    Ver perfil <ChevronRight size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS FOTO ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1590490360182-c33d57733427?auto=format&fit=crop&q=80&w=1600" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0 bg-orange-950/55" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-8 py-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { icon: Users,  value: '+95k', label: 'Miembros activos',  bg: 'bg-orange-100' },
              { icon: Heart,  value: '12k',  label: 'Posts por día',     bg: 'bg-amber-100'  },
              { icon: Globe,  value: '240+', label: 'Grupos de raza',    bg: 'bg-rose-100'   },
              { icon: Trophy, value: '1.2k', label: 'AmigoPoints récord', bg: 'bg-orange-100' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-[2.5rem] p-5 lg:p-7 flex flex-col items-center text-center`}>
                <div className="w-11 h-11 bg-orange-500 rounded-full flex items-center justify-center mb-3 shadow-md">
                  <s.icon size={18} className="text-white" />
                </div>
                <p className="text-3xl lg:text-4xl font-black text-slate-900">{s.value}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1 leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INFO PANELS ── */}
      <section className="py-16 bg-[#FEF3E8]">
        <div className="max-w-6xl mx-auto px-8">
          <p className="text-[10px] font-black text-orange-500 uppercase tracking-[0.3em] mb-10">Qué encontrarás en la comunidad</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Users,    topic: 'Red social para mascotas',  text: 'Crea el perfil de tu mascota con foto y personalidad. Comparte fotos y videos con stickers exclusivos de Amigo Peludo, sigue a otras mascotas y recibe "patitas" (likes).' },
              { icon: Globe,    topic: 'Grupos por raza y ciudad',  text: 'Más de 240 grupos activos por raza, ciudad y estilo de vida. Encuentra consejos, comparte experiencias y organiza salidas con dueños que comparten tu misma raza.' },
              { icon: Heart,    topic: 'Playdates y encuentros',    text: 'Organiza encuentros de mascotas en parques, playas o cafeterías pet-friendly. El mapa de Amigo Peludo muestra los eventos activos en tu zona en tiempo real.' },
              { icon: Trophy,   topic: 'AmigoPoints y retos',       text: 'Gana AmigoPoints completando retos de adiestramiento, participando en la comunidad y siendo activo en la plataforma. Canjéalos por descuentos y beneficios reales.' },
              { icon: Shield,   topic: 'Red SOS comunitaria',       text: 'La comunidad es la primera línea de respuesta ante una mascota perdida. Con +95k miembros activos, el radio de respuesta SOS es de menos de 2 minutos en zonas urbanas.' },
              { icon: Sparkles, topic: 'Stickers exclusivos AP',    text: 'Amigo Peludo tiene una colección de stickers animados exclusivos para compartir el estado de ánimo de tu mascota. Se actualizan con nuevas colecciones cada temporada.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center mb-3">
                  <item.icon size={18} className="text-orange-600" />
                </div>
                <p className="text-[9px] font-black uppercase tracking-widest text-orange-600 mb-1.5">{item.topic}</p>
                <p className="text-[12px] font-medium text-slate-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
